/**
 * Hook for exporting the medical PA orders table data as a CSV download
 * via the Postgres search endpoint.
 *
 * @module useExportTableData
 */
import { useCallback, useState } from "react";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { downloadCsv } from "../utils/downloadCsv";
import {
  ExportFilterSummary,
  transformExportCsv,
} from "../utils/transformExportCsv";

/**
 * Provides an export trigger for the medical PA orders table that
 * fetches a CSV from the backend and downloads it with the given filename.
 *
 * @returns An object containing:
 *   - `exportData` — call with params, filename, and optional filter summary
 *     to trigger the CSV download
 *   - `loading` — whether an export request is in progress
 *   - `error` — error object if the export failed
 */
export const useExportTableData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const exportData = useCallback(
    async (
      params: SearchForMedicalPaOrdersFromPostgresParams,
      filename: string,
      filters?: ExportFilterSummary,
    ) => {
      setLoading(true);
      setError(null);

      try {
        const csvContent = await searchForMedicalPaOrdersFromPostgres(params);
        const transformedCsv = transformExportCsv(csvContent, {
          filters: filters ?? params,
        });
        downloadCsv(transformedCsv, filename);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { exportData, loading, error };
};
